import React, { FC } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import ControlButton from 'studio_react/common/components/VideoPlayer/ControlButton';
import { ReactComponent as ExpandIcon } from 'studio_react/svg/common/expand.svg';
import { ReactComponent as PauseIcon } from 'studio_react/svg/common/pause.svg';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';

const formatTime = (s: number): string => {
    if (!isFinite(s) || isNaN(s)) {
        return '0:00';
    }

    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);

    return `${m}:${sec.toString().padStart(2, '0')}`;
};

const sliderSx = {
    appearance: 'none',
    WebkitAppearance: 'none',
    height: '4px',
    borderRadius: '2px',
    outline: 'none',
    cursor: 'pointer',
    '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        appearance: 'none',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer'
    },
    '&::-moz-range-thumb': {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer',
        border: 'none'
    }
};

interface SlideshowControlsProps {
    isPlaying: boolean;
    currentTime: number;
    totalDuration: number;
    isFullscreen: boolean;
    onPlayPause: () => void;
    onSeek: (time: number) => void;
    onToggleFullscreen: () => void;
}

const SlideshowControls: FC<SlideshowControlsProps> = ({
    isPlaying,
    currentTime,
    totalDuration,
    isFullscreen,
    onPlayPause,
    onSeek,
    onToggleFullscreen
}) => {
    const fillPercent =
        totalDuration > 0 ? Math.min(100, Math.max(0, (currentTime / totalDuration) * 100)) : 0;

    return (
        <Box position="absolute" bottom={0} left={0} right={0} zIndex={2}>
            <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="120px"
                background="linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)"
                pointerEvents="none"
                zIndex={0}
            />

            <Flex
                direction="column"
                position="relative"
                zIndex={1}
                px={{ base: '8px', md: '15px' }}
                pb={{ base: '10px', md: '17px' }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                <Flex
                    h={{ base: '32px', md: '40px' }}
                    alignItems="center"
                    gap={{ base: '2px', md: '4px' }}
                    minW={0}
                >
                    <ControlButton
                        onClick={onPlayPause}
                        ariaLabel={isPlaying ? 'Pause' : 'Play'}
                        bg="rgba(0,0,0,0.6)"
                        w={{ base: '36px', md: '48px' }}
                        h={{ base: '32px', md: '40px' }}
                    >
                        {isPlaying ? (
                            <PauseIcon width={20} height={20} />
                        ) : (
                            <PlayIcon width={20} height={20} />
                        )}
                    </ControlButton>

                    <Flex
                        flex={1}
                        minW={0}
                        bg="rgba(0,0,0,0.6)"
                        px={{ base: '6px', md: '10px' }}
                        py="4px"
                        rounded="4px"
                        gap={{ base: '4px', md: '12px' }}
                        h={{ base: '32px', md: '40px' }}
                        alignItems="center"
                    >
                        <Box
                            as="input"
                            type="range"
                            min={0}
                            max={totalDuration || 1}
                            value={currentTime}
                            step={0.1}
                            aria-label="Seek"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onSeek(Number(e.target.value))
                            }
                            flex={1}
                            minW={0}
                            style={{
                                background: `linear-gradient(to right, white ${fillPercent}%, rgba(255,255,255,0.3) ${fillPercent}%)`
                            }}
                            sx={sliderSx}
                        />

                        <Text color="white" fontSize="14px" whiteSpace="nowrap" flexShrink={0}>
                            {formatTime(currentTime)} / {formatTime(totalDuration)}
                        </Text>

                        <ControlButton
                            onClick={onToggleFullscreen}
                            ariaLabel={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            <Box
                                w="24px"
                                h="24px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <ExpandIcon width={20} height={20} />
                            </Box>
                        </ControlButton>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

export default SlideshowControls;
